// What is left of this device's AI allowance (label scans), as the edge last reported it. The edge
// owns the count (apps/edge/src/aiQuota.ts); this is only a mirror so the scan screen can show how many
// scans remain and stop offering one that would be refused. Kept in localStorage so a reload does not
// forget a spent allowance; every access is guarded, so blocked storage only means a session-long memory.
import { AiQuota } from "@proef/contract";
import { useSyncExternalStore } from "react";
import type { Locale } from "./i18n";

const STORAGE_KEY = "proef.aiQuota";

/** The edge refused a scan because the rolling-24h allowance is spent; `resetAt` is when the next one frees up. */
export class AiQuotaError extends Error {
  readonly limit: number;
  readonly resetAt: string;
  constructor(limit: number, resetAt: string) {
    super("AI usage limit reached");
    this.name = "AiQuotaError";
    this.limit = limit;
    this.resetAt = resetAt;
  }
}

function readStored(): AiQuota | undefined {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return undefined;
    const parsed = AiQuota.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : undefined;
  } catch {
    // Blocked storage or a damaged value: nothing known, which only means no counter is shown.
    return undefined;
  }
}

let current: AiQuota | undefined = typeof window === "undefined" ? undefined : readStored();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** Stores what the edge just reported (a success's quota, or a 429's remaining 0). */
export function recordAiQuota(quota: AiQuota): void {
  current = quota;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(quota));
  } catch {
    // Not persisting only loses the counter across a reload.
  }
  notify();
}

/** The last reported quota, or undefined when none is known or its reset has passed (the window has moved on since). */
export function getAiQuota(): AiQuota | undefined {
  if (current !== undefined && Date.parse(current.resetAt) <= Date.now()) return undefined;
  return current;
}

/** True while the edge has said no scans are left and the next one is still ahead. */
export function isAiQuotaSpent(): boolean {
  return getAiQuota()?.remaining === 0;
}

/** When the allowance frees up, for a person to read: the clock time, with the weekday when it is not today. */
export function formatResetTime(locale: Locale, resetAt: string): string {
  const at = new Date(resetAt);
  const today = new Date();
  const sameDay = at.toDateString() === today.toDateString();
  return new Intl.DateTimeFormat(locale, {
    ...(sameDay ? {} : { weekday: "short" }),
    hour: "2-digit",
    minute: "2-digit",
  }).format(at);
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The last reported quota, re-rendering when a scan reports a new one. */
export function useAiQuota(): AiQuota | undefined {
  // getAiQuota returns the stored object itself, so the snapshot is stable between changes.
  return useSyncExternalStore(subscribe, getAiQuota, () => undefined);
}

/** Tests: forgets the remembered quota; `keepStorage` re-reads it from storage instead, as a reload would. */
export function resetAiQuota(options: { keepStorage?: boolean } = {}): void {
  current = options.keepStorage === true ? readStored() : undefined;
  if (options.keepStorage !== true) {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Nothing stored to forget.
    }
  }
  notify();
}
